import { type PaymentAccountCard } from "@/features/payments";

export enum PhysicalCardShippingStatus {
  NEW = "NEW",
  PENDING = "PENDING",
  SENT_TO_PRINTER = "SENT_TO_PRINTER",
  APPROVED = "APPROVED",
  SHIPPED = "SHIPPED",
  CANCELED = "CANCELED",
  SHIP_FAILED = "SHIP_FAILED",
}

/**
 * Type guard to check if a string is a valid PhysicalCardShippingStatus
 */
function isPhysicalCardShippingStatus(
  status: string
): status is PhysicalCardShippingStatus {
  return Object.values(PhysicalCardShippingStatus).includes(
    status as PhysicalCardShippingStatus
  );
}

/**
 * Converts a string to a PhysicalCardShippingStatus. PPS maps each provider's
 * own shipping states onto this vocabulary and passes anything it cannot map
 * through unchanged, so an unrecognised value is data the app does not know
 * rather than a programming error: it resolves to null and the tracker is not
 * drawn.
 * @param status - The status string from the backend
 * @returns The matching status, or null when the app does not recognise it
 */
export function getPhysicalCardShippingStatus(
  status: string
): PhysicalCardShippingStatus | null {
  return isPhysicalCardShippingStatus(status) ? status : null;
}

/**
 * The card's shipping status. An absent value means nothing has shipped yet,
 * which is the start of the journey; an unrecognised one resolves to null.
 */
export function getCardShippingStatus(
  card: Pick<PaymentAccountCard, "delivery_status" | "physical_order_status">
): PhysicalCardShippingStatus | null {
  // FIXME: delivery_status was removed from PPS
  const rawStatus = card.delivery_status ?? card.physical_order_status ?? "";
  return rawStatus
    ? getPhysicalCardShippingStatus(rawStatus)
    : PhysicalCardShippingStatus.NEW;
}

/**
 * Whether a physical card awaiting activation shows the shipping tracker rather
 * than its own details. A card whose status the app does not recognise but which
 * already carries its last four digits is a working card, so its details are
 * shown instead of a tracker that cannot be drawn.
 */
export function showsShippingStatus(
  card: Pick<
    PaymentAccountCard,
    "delivery_status" | "physical_order_status" | "card_number"
  >
): boolean {
  return getCardShippingStatus(card) !== null || !card.card_number;
}
